import * as React from 'react';
import { useState, useContext, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import MicrowaveIcon from '@mui/icons-material/Microwave';
import type { Router, Navigation, Session, } from '@toolpad/core';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { AccountBalance, Analytics, Dashboard, OfflineBoltOutlined, Person, PieChart, Settings, Wallet, AddBusiness, CurrencyExchange, AttachMoney, FactCheck, Grading, DoneAll, Fax, InstallMobile, SettingsInputComponent, Cloud } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import StorageIcon from '@mui/icons-material/Storage';
import { appContext } from '../App';
import { CustomNotifications } from './Components/GlobalComponents/Nofications';
import { signOut } from '../helpers/AuthHelper';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ApiIcon from '@mui/icons-material/Api';
import Cookies from 'js-cookie';

interface NotificationModalProps {
    open: boolean;
    onClose: () => void;
}

const ADMIN_NAVIGATION: Navigation = [
    {
        segment: 'customers',
        title: 'Customers',
        icon: <GroupIcon />,
    },
    {
        segment: 'machines',
        title: 'Machines',
        icon: <StorageIcon />,
    },
    {
        segment: 'invoices',
        title: 'Invoices',
        icon: <DescriptionIcon />,
    },
    {
        segment: 'monitor',
        title: 'Hash Monitor',
        icon: <MonitorHeartIcon />,
    },
    {
        segment: 'payments',
        title: 'Payments',
        icon: <LocalAtmIcon />,
    },
    {
        segment: 'proxy-settlements',
        title: 'Proxy Rewards',
        icon: <FactCheck />,
    },
    {
        segment: 'report',
        title: 'Financial Reports',
        icon: <Analytics />,
    },
    {
        segment: 'accounts',
        title: 'Reseller Accounts',
        icon: <AccountBalance />,
    },
    {
        segment: 'wallets',
        title: 'Wallets',
        icon: <Wallet />,
    },
    {
        segment: 'hashboard',
        title: 'Hashrate Expense',
        icon: <Dashboard />,
    },
    {
        segment: 'simulator',
        title: 'Simulator',
        icon: <PieChart />,
    },
    {
        segment: 'priceprediction',
        title: 'Price Prediction',
        icon: <CurrencyExchange />,
    },
    {
        kind: 'divider',
    },
    {
        segment: 'apiCalls',
        title: 'API',
        icon: <ApiIcon />,
    },
    {
        kind: 'divider',
    },
    {
        segment: 'sales',
        title: 'Sales',
        icon: <AttachMoney />,
        children: [
            {
                segment: 'prices',
                title: 'Prices',
                icon: <CurrencyExchange />,
            },
        ],
    },
    {
        kind: 'divider',
    },
    {
        segment: 'audit',
        title: 'Audit',
        icon: <FactCheck />,
    },
    {
        segment: 'validator',
        title: 'Finance Validator',
        icon: <Grading />,
    },
    {
        segment: 'assets',
        title: 'Asset Management',
        icon: <Fax />,
    },
    {
        kind: 'divider',
    },
    {
        segment: 'settings',
        title: 'Settings',
        icon: <Settings />,
        children: [
            {
                segment: 'machines',
                title: 'Machines Model',
                icon: <MicrowaveIcon />,
            },
            {
                segment: 'authentication',
                title: 'Authentication',
                icon: <DomainVerificationIcon />,
            },
            {
                segment: 'nicehash',
                title: 'Nicehash',
                icon: <OfflineBoltOutlined />,
                children: [
                    {
                        segment: 'price-configuration',
                        title: 'Price Configuration',
                        icon: <SettingsInputComponent />,
                    },
                ],
            },
            {
                segment: 'cloud-order',
                title: 'Cloud Order',
                icon: <Cloud />,
                children: [
                    {
                        segment: 'settings',
                        title: 'Cloud Order Setting',
                        icon: <AddBusiness />,
                    },
                    {
                        segment: 'orders',
                        title: 'Cloud Orders',
                        icon: <Cloud />,
                    },
                ],
            },
            {
                segment: 'reseller',
                title: 'Reseller',
                icon: <Person />,
            },
            {
                segment: 'hostingSetup',
                title: 'Hosting Setup',
                icon: <InstallMobile />,
            },
            {
                segment: 'equalizer',
                title: 'Equalizer Organizations',
                icon: <GroupIcon />,
            },
            {
                segment: 'external-tenants',
                title: 'External Tenants',
                icon: <ApiIcon />,
            },
            {
                segment: 'partners',
                title: 'Partners',
                icon: <GroupIcon />,
            },
            {
                segment: 'price-prediction',
                title: 'Price Prediction',
                icon: <CurrencyExchange />,
            },
        ],
    },
];

const READONLY_NAVIGATION: Navigation = [
    {
        segment: 'customers',
        title: 'Customers',
        icon: <GroupIcon />,
    },
    {
        segment: 'machines',
        title: 'Machines',
        icon: <StorageIcon />,
    },
    {
        segment: 'invoices',
        title: 'Invoices',
        icon: <DescriptionIcon />,
    },
    {
        segment: 'payments',
        title: 'Payments',
        icon: <LocalAtmIcon />,
    },
    {
        segment: 'proxy-settlements',
        title: 'Proxy Rewards',
        icon: <FactCheck />,
    },
    {
        segment: 'report',
        title: 'Financial Reports',
        icon: <Analytics />,
    },
    {
        segment: 'wallets',
        title: 'Wallets',
        icon: <Wallet />,
    },
    {
        segment: 'simulator',
        title: 'Simulator',
        icon: <PieChart />,
    },
    {
        segment: 'priceprediction',
        title: 'Price Prediction',
        icon: <CurrencyExchange />,
    },
    {
        kind: 'divider',
    },
    {
        segment: 'sales',
        title: 'Sales',
        icon: <AttachMoney />,
        children: [
            {
                segment: 'prices',
                title: 'Prices',
                icon: <CurrencyExchange />,
            },
        ],
    },
    {
        segment: 'settings',
        title: 'Settings',
        icon: <Settings />,
        children: [
            {
                segment: 'machines',
                title: 'Machines Model',
                icon: <MicrowaveIcon />,
            },
            {
                segment: 'nicehash',
                title: 'Nicehash',
                icon: <OfflineBoltOutlined />,
                children: [
                    {
                        segment: 'price-configuration',
                        title: 'Price Configuration',
                        icon: <SettingsInputComponent />,
                    }
                ],
            }
        ],
    },
];

const RESELLER_NAVIGATION: Navigation = [
    {
        segment: 'reseller-dashboard',
        title: 'My Machines',
        icon: <StorageIcon />,
    }
];

const demoTheme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    colorSchemes: { light: true, dark: true },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 600,
            lg: 1200,
            xl: 1536,
        },
    },
});

interface DashboardLayoutBasicProps {
    children?: React.ReactNode;
    window?: () => Window;
}

export default function DashboardLayoutBasic(props: DashboardLayoutBasicProps) {
    const { children, window } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthVerified, setIsAuthVerified, authUserName, authRole } = useContext(appContext);


    const [pathname, setPathname] = useState(location.pathname);

    const [session, setSession] = useState<Session | null>({
        user: {
            name: authUserName,
            // image: '',
        },
    });

    const authentication = useMemo(() => {
        return {
            signIn: () => { },
            signOut: () => {
                signOut()
                setIsAuthVerified((x: boolean) => !x)
            },
        };
    }, []);

    React.useEffect(() => {
        navigate(pathname);
    }, [pathname]);

    React.useEffect(() => {
        setPathname(location.pathname);
    }, [location.pathname]);

    const router = React.useMemo<Router>(() => ({
        pathname,
        searchParams: new URLSearchParams(),
        navigate: (path) => setPathname(String(path)),
    }), [pathname]);

    React.useEffect(() => {
        navigate(pathname)
    }, [pathname])

    const demoWindow = window !== undefined ? window() : undefined;



    return (
        <AppProvider
            session={session}
            navigation={authRole === 'ADMIN' ? ADMIN_NAVIGATION : (authRole === 'RESELLER' ? RESELLER_NAVIGATION : READONLY_NAVIGATION)}
            router={router}
            theme={demoTheme}
            window={demoWindow}
            authentication={authentication}
            branding={{
                logo: <img src="https://mui.com/static/logo.png" alt="MUI logo" />,
                title: 'MY DASHBOARD',
            }}
        >
            <DashboardLayout>
                <React.Suspense fallback={<div>Loading...</div>}>
                    {children}
                </React.Suspense>
                <CustomNotifications />
            </DashboardLayout>
        </AppProvider>
    );
}
